import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Plane, Shield } from 'lucide-react';
import { SEOLayout, AlertBox } from '../../components/SEOLayout';
import { allBreedsData } from '../../data/seoDataMerged';
import { IataRequirementsList, IataSourceLink, SNUB_NOSED_SLUGS } from '../../data/iataRules';
import { Button } from '../../components/ui/button';
import NotFound from '../NotFound';

const BreedDetail = () => {
  const { slug } = useParams();
  const breed = allBreedsData[slug];
  if (!breed) return <NotFound />;
  const snub = SNUB_NOSED_SLUGS.includes(slug);
  const animal = breed.type === 'cat' ? 'cat' : 'dog';

  return (
    <SEOLayout
      title={breed.seoTitle}
      description={breed.seoDescription}
      breadcrumbs={[{ label: 'Breeds', href: '/breeds' }, { label: breed.name }]}
    >
      <div className="mb-8 max-w-3xl">
        <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{breed.name} Crate Size for Flying</h1>
        <p className="text-lg text-slate-700">
          The crate size depends on your own {breed.name}'s measurements, not on a breed average.
          Measure your {animal} standing and apply the IATA formula below, or use the free calculator.
        </p>
      </div>

      {snub && (
        <AlertBox type="warning" title={`${breed.name}: snub-nosed breed`}>
          <p>IATA asks for a crate 10% larger for snub-nosed (brachycephalic) breeds. Many airlines also restrict these breeds in the hold: check your airline's official policy.</p>
          <IataSourceLink className="mt-2" />
        </AlertBox>
      )}

      <div className="grid lg:grid-cols-3 gap-8 my-8">
        <section data-testid="breed-iata-size" className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-6">
          <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-600" /> Crate size for a {breed.name}: the IATA formula
          </h2>
          <IataRequirementsList />
          <div className="flex flex-wrap gap-4 mt-4 text-sm font-medium">
            <Link data-testid="breed-calculator-link" to="/guides/iata-crate-size-calculator" className="text-blue-600">Free IATA crate size calculator →</Link>
            <Link to="/guides/iata-crate-requirements-2026" className="text-blue-600">IATA crate requirements checklist →</Link>
          </div>
        </section>
        <aside className="space-y-6">
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-6 text-white">
            <h3 className="text-xl font-bold mb-3">Check Your Crate</h3>
            <p className="text-blue-100 text-sm mb-4">Enter your {breed.name}'s measurements and upload 3 photos. We apply the IATA size formula and check what is visible.</p>
            <Link to="/"><Button className="w-full bg-white text-blue-600 hover:bg-blue-50">Check My Crate - $39</Button></Link>
            <p className="text-blue-100 text-xs mt-3">Independent service, not affiliated with IATA or any airline.</p>
          </div>
          <div className="bg-slate-100 rounded-xl p-6 text-sm text-slate-700">
            <h3 className="font-bold text-slate-900 mb-2 flex items-center gap-2"><Plane className="w-4 h-4" /> Airline rules</h3>
            <p>Breed restrictions differ by airline and change often. Check the official policy of your airline before booking.</p>
            <Link to="/airlines" className="inline-block mt-2 text-blue-600 font-medium">Airline pages →</Link>
          </div>
        </aside>
      </div>
    </SEOLayout>
  );
};

export default BreedDetail;
